// Portfel — analyses: P&L attribution, bond calendar, safe alternative, projection.
import { addDays, diffDays, xirr } from "./engine";
import type {
  AccountsConfig, AggregateResult, Attribution, AttributionBreakdown, AttributionRow, BondCalendar, BondEvent, BondInfo, BondKind,
  BondParams, BondPeriod, BondRates, ContributionRow, Contributions, DailyByKey, DailyKeyRow, Day, GroupYearReturns, HarvestCandidate,
  Model, ModelData, ProjectionParams, ProjectionPoint, ProjectionResult, ProjectionScenario, RetirementContrib, RetirementFlows,
  RetirementLimit, RollingPoint, SafeAlternative, TerCosts, TerRow, XirrFlow,
} from "./types";

const EPS = 1e-9;
const DAY = 86400000;
// rounding to the grosz (cent), "half up", on the number's decimal representation (like Decimal(repr(x)) in Python)
export const r2 = (x: number): number => {
  if (!Number.isFinite(x)) return x;
  const neg = x < 0;
  const str = Math.abs(x).toString();
  if (str.includes("e")) return Math.round(x * 100) / 100;
  const [ip, fp = ""] = str.split(".");
  const f = fp + "000";
  let cents = BigInt(ip + f.slice(0, 2));
  if (f.charCodeAt(2) >= 53) cents += 1n;
  const v = Number(cents) / 100;
  return neg && v !== 0 ? -v : v;
};
const dayMs = (d: Day): number => Date.UTC(+d.slice(0, 4), +d.slice(5, 7) - 1, +d.slice(8, 10));

// ------------------------------------------------------------------ Polish treasury bonds (port of importers/bonds_pl.py)
const BOND_RE = /^(EDO|ROD|ROS|COI)(\d{2})(\d{2})_(\d{8})$/;
export const BOND_YEARS: Record<BondKind, number> = { EDO: 10, ROD: 12, ROS: 6, COI: 4 };
export function bondInfo(sym: string | null | undefined): BondInfo | null {
  const m = BOND_RE.exec(sym || "");
  if (!m) return null;
  const d = m[4];
  return { kind: m[1] as BondKind, series: m[1] + m[2] + m[3], bought: `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6, 8)}` };
}
function anniv(d: Day, k: number): Day {
  const y = +d.slice(0, 4) + k;
  let md = d.slice(5);
  if (md === "02-29") md = "02-28";
  return `${y}-${md}`;
}
// cpi: {"YYYY-MM": 103.4, "YYYY-01F": ...}; futureCpi: y/y index (e.g. 102.5) for periods without GUS data
// params: [1st-period interest rate, margin] as fractions (0.068, 0.02)
export function bondPeriods(info: Pick<BondInfo, "kind" | "bought">, params: BondRates, cpi: Readonly<Record<string, number>>, futureCpi: number | null = null): BondPeriod[] {
  const [first, margin] = params;
  const months = Object.keys(cpi).filter((k) => !k.endsWith("F")).sort();
  const lastKnown = months.length ? cpi[months[months.length - 1]] : 100;
  const out: BondPeriod[] = [];
  for (let k = 0; k < BOND_YEARS[info.kind]; k++) {
    const s = anniv(info.bought, k), e = anniv(info.bought, k + 1);
    if (k === 0) { out.push({ s, e, r: first, est: false }); continue; }
    let y = +s.slice(0, 4), m = +s.slice(5, 7) - 2;
    while (m <= 0) { m += 12; y--; }
    let key = `${y}-${String(m).padStart(2, "0")}`;
    if (m === 1 && cpi[key + "F"] !== undefined) key += "F";
    const est = cpi[key] === undefined;
    const val = est ? (futureCpi ?? lastKnown) : cpi[key];
    out.push({ s, e, r: Math.round((Math.max(0, val - 100) / 100 + margin) * 1e6) / 1e6, est });
  }
  return out;
}
// value of a single bond (excluding paid-out coupons) as of day d
export function bondValue(info: Pick<BondInfo, "kind" | "bought">, periods: readonly BondPeriod[], d: Day): number | null {
  if (d < info.bought) return null;
  let base = 100;
  for (const p of periods) {
    if (d >= p.e) { if (info.kind !== "COI") base *= 1 + p.r; continue; }
    return r2(base * (1 + p.r * diffDays(p.s, d) / diffDays(p.s, p.e)));
  }
  return r2(base);
}
const pct2frac = (arr: BondRates): [number, number] => [arr[0] / 100, arr[1] / 100];
export function lastCpiYoY(bp: BondParams | null | undefined): { month: string; value: number } | null {
  const cpi = bp?.cpi || {};
  const months = Object.keys(cpi).filter((k) => !k.endsWith("F")).sort();
  return months.length ? { month: months[months.length - 1], value: cpi[months[months.length - 1]] } : null;
}

// ------------------------------------------------------------------ (2) bond cash flow calendar
export function bondCalendar(model: Model, accountSet: Iterable<string>, bp: BondParams | null | undefined, opts: { futureInflation?: number | null } = {}): BondCalendar {
  const S = new Set(accountSet);
  const today = model.today;
  const taxRate = model.taxRate ?? 0.19;
  const futureCpi = opts.futureInflation !== undefined && opts.futureInflation !== null ? 100 + opts.futureInflation * 100 : null;
  const events: BondEvent[] = [], missing = new Set<string>();
  for (const [pk, lots] of Object.entries(model.lotsByPos)) {
    const [a, s] = pk.split("|");
    if (!S.has(a)) continue;
    const info = bondInfo(s);
    if (!info) continue;
    const qty = lots.reduce((t, l) => t + (l.qty > EPS ? l.qty : 0), 0);
    if (qty <= EPS) continue;
    const cost = lots.reduce((t, l) => t + (l.qty > EPS ? l.costPLN : 0), 0);
    const par = bp?.series?.[info.series];
    if (!par) { missing.add(info.series); continue; }
    const per = bondPeriods(info, pct2frac(par), bp?.cpi || {}, futureCpi);
    const exempt = model.taxExempt.has(a);
    const maturity = anniv(info.bought, BOND_YEARS[info.kind]);
    const base = { account: a, exempt, symbol: s, series: info.series, kind: info.kind, qty, bought: info.bought, maturity };
    if (info.kind === "COI") {
      for (const p of per) {
        if (p.e <= today) continue;
        const gross = r2(r2(100 * p.r) * qty);
        const tax = exempt ? 0 : r2(gross * taxRate);
        events.push({ ...base, date: p.e, type: "coupon", rate: p.r, gross, interest: gross, principal: 0, tax, net: gross - tax, est: p.est });
      }
      if (maturity > today) events.push({ ...base, date: maturity, type: "redemption", gross: 100 * qty, interest: 0, principal: 100 * qty, tax: 0, net: 100 * qty, est: false });
    } else if (maturity > today) {
      const bv = bondValue(info, per, maturity); // never null (redemption date > purchase date)
      const final = r2((bv ?? 0) * qty);
      const interest = final - cost;
      const tax = exempt ? 0 : r2(Math.max(0, interest) * taxRate);
      const est = per.some((p) => p.est);
      events.push({ ...base, date: maturity, type: "redemption", gross: final, interest, principal: cost, tax, net: final - tax, est, rateNow: per.find((p) => p.s <= today && today < p.e)?.r ?? null });
    }
  }
  events.sort((x, y) => (x.date < y.date ? -1 : x.date > y.date ? 1 : x.symbol < y.symbol ? -1 : 1));
  return { events, missing: [...missing] };
}

// ------------------------------------------------------------------ (1) where the result came from
// Breakdown of the period's gross result (change in value − net contributions) into components. Daily identity:
//  securities: q[i-1]·(P[i]−P[i-1])·X[i-1] (price) + q[i-1]·P[i]·(X[i]−X[i-1]) (exchange rate) + transactions at the closing price,
//  foreign-currency cash: balance[i-1]·(X[i]−X[i-1]); income, commissions, taxes — from transactions.
type AttributionDraft = Omit<AttributionRow, "total">;
export function attribution(model: Model, accountSet: Iterable<string>, s0: number, e: number): Attribution {
  const S = new Set(accountSet);
  const { acc, instr, priceArr, priceCcy, fxArr, idx } = model;
  const fxv = (c: string, i: number): number => (c === "PLN" ? 1 : fxArr[c] ? fxArr[c][i] : NaN);
  const B: AttributionBreakdown = { price: 0, fx: 0, fxCash: 0, bond: 0, dividends: 0, cashInterest: 0, fees: 0, taxes: 0, other: 0 };
  const fxByCcy: Record<string, number> = {};
  const rows: Record<string, AttributionDraft> = {};
  const R = (s: string): AttributionDraft => {
    const k = instr[s]?.key || s;
    return (rows[k] ||= { key: k, name: instr[s]?.name || s, isBond: instr[s]?.assetClass === "BOND", bondKind: bondInfo(s)?.kind || "", price: 0, fx: 0, income: 0, fees: 0, taxes: 0 });
  };
  for (const a of S) {
    const A = acc[a];
    if (!A) continue;
    for (const [s, q] of Object.entries(A.qty)) {
      const P = priceArr[s];
      if (!P) continue;
      const c = priceCcy[s];
      const bond = instr[s]?.assetClass === "BOND";
      let dp = 0, dx = 0;
      for (let i = s0 + 1; i <= e; i++) {
        const qp = q[i - 1];
        if (qp === 0) continue;
        const p0 = P[i - 1], p1 = P[i], x0 = fxv(c, i - 1), x1 = fxv(c, i);
        if (isNaN(p0) || isNaN(p1) || isNaN(x0) || isNaN(x1)) continue;
        dp += qp * (p1 - p0) * x0;
        dx += qp * p1 * (x1 - x0);
      }
      if (!dp && !dx) continue;
      const r = R(s);
      r.price += dp; r.fx += dx;
      if (bond) B.bond += dp + dx; else { B.price += dp; B.fx += dx; if (c !== "PLN") fxByCcy[c] = (fxByCcy[c] || 0) + dx; }
    }
    for (const [c, q] of Object.entries(A.cashCcy)) {
      if (c === "PLN") continue;
      let dx = 0;
      for (let i = s0 + 1; i <= e; i++) {
        if (!q[i - 1]) continue;
        const x0 = fxv(c, i - 1), x1 = fxv(c, i);
        if (!isNaN(x0) && !isNaN(x1)) dx += q[i - 1] * (x1 - x0);
      }
      B.fxCash += dx;
      if (dx) fxByCcy[c] = (fxByCcy[c] || 0) + dx;
    }
  }
  for (const t of model.txs) {
    if (!S.has(t.account)) continue;
    const i = idx(t.day);
    if (i <= s0 || i > e) continue;
    const x = fxv(t.currency, i);
    if (isNaN(x)) continue;
    switch (t.type) {
      case "BUY":
      case "SELL": {
        const pc = model.pPLN(t.symbol, i);
        const sign = t.type === "BUY" ? 1 : -1;
        const exec = (isNaN(pc) ? 0 : sign * t.qty * pc) + (t.amt + t.fee) * x; // difference between the transaction price and the closing price
        const fee = t.fee * x;
        const r = R(t.symbol);
        r.price += exec; r.fees -= fee;
        B.fees -= fee;
        if (r.isBond) B.bond += exec; else B.price += exec;
        break;
      }
      case "DIVIDEND":
      case "INTEREST": {
        const gross = (t.amt + t.taxw) * x, w = t.taxw * x;
        B.taxes -= w;
        if (t.symbol) {
          const r = R(t.symbol);
          r.income += gross; r.taxes -= w;
          if (r.isBond) B.bond += gross; else if (t.type === "DIVIDEND") B.dividends += gross; else B.cashInterest += gross;
        } else if (t.type === "DIVIDEND") B.dividends += gross; else B.cashInterest += gross;
        break;
      }
      case "TAX":
        B.taxes += t.amt * x;
        if (t.symbol) R(t.symbol).taxes += t.amt * x;
        break;
      case "FEE":
        B.fees += t.amt * x;
        break;
      case "ADJUSTMENT":
        B.other += t.amt * x;
        break;
      default:
        break;
    }
  }
  return { B, fxByCcy, rows: Object.values(rows).map((r) => ({ ...r, total: r.price + r.fx + r.income + r.fees + r.taxes })) };
}

// ------------------------------------------------------------------ (4) safe alternative: the same contributions in EDO / a bank deposit
interface EdoPick {
  params: [number, number];
  approx: boolean;
  month: string;
  from?: string;
}
function edoPicker(bp: BondParams | null | undefined): (day: Day) => EdoPick {
  const byMonth: Record<string, BondRates> = {}; // issue "YYYY-MM" -> [first, margin]
  for (const [k, v] of Object.entries(bp?.series || {})) {
    const m = /^EDO(\d{2})(\d{2})$/.exec(k);
    if (m) byMonth[`${2000 + +m[2] - 10}-${m[1]}`] = v;
  }
  const keys = Object.keys(byMonth).sort();
  const mIdx = (ym: string): number => +ym.slice(0, 4) * 12 + +ym.slice(5, 7) - 1;
  return (day) => {
    const ym = day.slice(0, 7);
    if (byMonth[ym]) return { params: pct2frac(byMonth[ym]), approx: false, month: ym };
    if (!keys.length) return { params: [0.06, 0.02], approx: true, month: ym };
    let best = keys[0];
    for (const k of keys) if (Math.abs(mIdx(k) - mIdx(ym)) < Math.abs(mIdx(best) - mIdx(ym))) best = k;
    return { params: pct2frac(byMonth[best]), approx: true, month: ym, from: best };
  };
}
// fast lot valuation for consecutive (increasing) days: periods as day numbers, pointer to the current period
function fastLot(info: Pick<BondInfo, "bought">, per: readonly BondPeriod[]): (d: number) => number {
  const dn = (d: Day): number => dayMs(d) / DAY;
  const ps: { s: number; e: number; r: number; base: number }[] = [];
  let base = 100;
  for (const p of per) { ps.push({ s: dn(p.s), e: dn(p.e), r: p.r, base }); base *= 1 + p.r; }
  const bought = dn(info.bought), finalV = r2(base);
  let j = 0;
  return (d) => {
    if (d < bought) return 0;
    while (j < ps.length && d >= ps[j].e) j++;
    if (j >= ps.length) return finalV;
    const p = ps[j];
    return r2(p.base * (1 + p.r * (d - p.s) / (p.e - p.s)));
  };
}
type RatePoints = readonly (readonly [Day, number])[];
function refRateAt(nbpRef: RatePoints | null | undefined): (d: Day) => number {
  const pts = (nbpRef || []).slice().sort((a, b) => (a[0] < b[0] ? -1 : 1));
  let j = 0;
  return (d) => {
    if (!pts.length) return 0;
    while (j < pts.length - 1 && pts[j + 1][0] <= d) j++;
    while (j > 0 && pts[j][0] > d) j--;
    return pts[j][0] <= d ? pts[j][1] : pts[0][1];
  };
}
export function safeAlternative(model: Model, agg: AggregateResult, s0: number, e: number, bp: BondParams | null | undefined, nbpRef: RatePoints | null | undefined,
  opts: { spread?: number } = {}): SafeAlternative {
  const { days, acc } = model;
  const S = new Set(agg.accounts);
  const exempt = model.taxExempt;
  const taxRate = model.taxRate ?? 0.19;
  const spread = opts.spread ?? -1;
  const n = e - s0 + 1;
  // flows and starting value split into taxable / IKE-IKZE accounts
  const G: Record<"T" | "X", { F: Float64Array; V0: number; taxed: boolean }> = { T: { F: new Float64Array(n), V0: 0, taxed: true }, X: { F: new Float64Array(n), V0: 0, taxed: false } };
  for (const a of S) {
    const A = acc[a];
    if (!A) continue;
    const g = exempt.has(a) ? G.X : G.T;
    g.V0 += A.sec[s0] + A.cash[s0];
    for (let i = s0 + 1; i <= e; i++) g.F[i - s0] += A.flow[i];
  }
  for (const x of model.xferFlows) {
    if (x.i <= s0 || x.i > e || !S.has(x.account)) continue;
    if (!S.has(x.counterpart) || exempt.has(x.account) !== exempt.has(x.counterpart)) (exempt.has(x.account) ? G.X : G.T).F[x.i - s0] += x.v;
  }
  const pick = edoPicker(bp);
  const cpi = bp?.cpi || {};
  const ref = refRateAt(nbpRef);
  const approx = new Set<string>();
  const edo = new Float64Array(n), edoNet = new Float64Array(n), dep = new Float64Array(n);
  let lotsCount = 0;
  for (const g of Object.values(G)) {
    // EDO: each contribution buys bonds from that month's issue; a withdrawal redeems all lots proportionally
    const lots: { units: number; cost: number; val: (d: number) => number }[] = [];
    const buy = (k: number, v: number): void => {
      const pk = pick(days[s0 + k]);
      if (pk.approx) approx.add(pk.month);
      const info = { kind: "EDO" as const, bought: days[s0 + k] };
      lots.push({ units: v / 100, cost: v, val: fastLot(info, bondPeriods(info, pk.params, cpi)) });
      lotsCount++;
    };
    // bank deposit / savings account: NBP reference rate + spread, monthly compounding, Belka withheld at compounding
    let bal = 0, accr = 0;
    const capitalize = (): void => { const tax = g.taxed ? accr * taxRate : 0; bal += accr - tax; accr = 0; };
    for (let k = 0; k < n; k++) {
      const d = days[s0 + k];
      const dn = dayMs(d) / DAY;
      const f = k === 0 ? g.V0 : g.F[k];
      if (k > 0) {
        const rate = Math.max(0, ref(d) + spread) / 100;
        accr += bal * rate / 365;
        if (d.slice(8) === "01") capitalize();
      }
      if (f > 0.005) { buy(k, f); bal += f; }
      else if (f < -0.005) {
        let tot = 0;
        for (const l of lots) tot += l.units * l.val(dn);
        const q = tot > 0 ? Math.min(1, -f / tot) : 0;
        for (const l of lots) { l.units *= 1 - q; l.cost *= 1 - q; }
        capitalize();
        bal = Math.max(0, bal + f);
      }
      let v = 0, c = 0;
      for (const l of lots) { if (l.units <= 0) continue; v += l.units * l.val(dn); c += l.cost; }
      edo[k] += v;
      edoNet[k] += g.taxed ? v - taxRate * Math.max(0, v - c) : v;
      dep[k] += bal + accr * (g.taxed ? 1 - taxRate : 1);
    }
  }
  // XIRR of each path for the same flows
  const t0 = dayMs(days[s0]);
  const V0 = G.T.V0 + G.X.V0;
  const flowsBase: XirrFlow[] = [];
  if (V0 > 0.01) flowsBase.push({ t: t0, v: -V0 });
  for (let k = 1; k < n; k++) { const f = G.T.F[k] + G.X.F[k]; if (Math.abs(f) > 0.001) flowsBase.push({ t: t0 + k * DAY, v: -f }); }
  const irr = (endV: number): number | null => xirr([...flowsBase, { t: t0 + (n - 1) * DAY, v: endV }]);
  return { n, edo, edoNet, dep, approx: [...approx].sort(), lots: lotsCount, irr, V0 };
}

// ------------------------------------------------------------------ (5) projection
// scenarios: real annual rates of return; inflation: assumption; monthly contributions (in today's PLN, optionally inflation-indexed)
export function projection<Sc extends ProjectionScenario>({ start, monthly, years, scenarios, inflation, growContrib = true, taxShare = 0, taxRate = 0.19, taxOn = false }: ProjectionParams<Sc>): ProjectionResult<Sc>[] {
  const months = Math.round(years * 12);
  const out = scenarios.map((sc): ProjectionResult<Sc> => {
    const rn = (1 + sc.real) * (1 + inflation) - 1;
    const mr = Math.pow(1 + rn, 1 / 12) - 1;
    let v = start, paid = start, paidReal = start;
    const pts: ProjectionPoint[] = [{ m: 0, nominal: v, real: v, paid, paidReal, net: v, netReal: v }];
    for (let k = 1; k <= months; k++) {
      const defl = Math.pow(1 + inflation, k / 12);
      const c = monthly * (growContrib ? Math.pow(1 + inflation, Math.floor((k - 1) / 12)) : 1);
      v = v * (1 + mr) + c;
      paid += c;
      paidReal += c / defl;
      const net = taxOn ? v - taxRate * Math.max(0, v - paid) * taxShare : v;
      pts.push({ m: k, nominal: v, real: v / defl, paid, paidReal, net, netReal: net / defl });
    }
    return { ...sc, nominalRate: rn, pts };
  });
  return out;
}

// ------------------------------------------------------------------ daily P&L and value per instrument (ISIN key)
// pl[t]: the day's P&L (price + exchange rate + transactions at close + income − commissions − withholding taxes), val[t]: end-of-day value,
// buy[t]: amount of purchases on the day (for the denominator of the group's return)
export function dailyByKey(model: Model, accountSet: Iterable<string>, s0: number, e: number): DailyByKey {
  const S = new Set(accountSet);
  const { acc, instr, priceArr, priceCcy, fxArr, idx } = model;
  const n = e - s0 + 1;
  const fxv = (c: string, i: number): number => (c === "PLN" ? 1 : fxArr[c] ? fxArr[c][i] : NaN);
  const keys: Record<string, DailyKeyRow> = {};
  const K = (s: string): DailyKeyRow => {
    const k = instr[s]?.key || s;
    return (keys[k] ||= { key: k, name: instr[s]?.name || s, group: instr[s]?.group || "Inne", isBond: instr[s]?.assetClass === "BOND", bondKind: bondInfo(s)?.kind || "",
      pl: new Float64Array(n), val: new Float64Array(n), buy: new Float64Array(n) });
  };
  for (const a of S) {
    const A = acc[a];
    if (!A) continue;
    for (const [s, q] of Object.entries(A.qty)) {
      const P = priceArr[s];
      if (!P) continue;
      const c = priceCcy[s];
      let row: DailyKeyRow | null = null;
      for (let i = s0; i <= e; i++) {
        const x1 = fxv(c, i), p1 = P[i];
        if (q[i] && !isNaN(p1) && !isNaN(x1)) (row ||= K(s)).val[i - s0] += q[i] * p1 * x1;
        if (i === s0) continue;
        const qp = q[i - 1];
        if (!qp) continue;
        const p0 = P[i - 1], x0 = fxv(c, i - 1);
        if (isNaN(p0) || isNaN(p1) || isNaN(x0) || isNaN(x1)) continue;
        (row ||= K(s)).pl[i - s0] += qp * (p1 * x1 - p0 * x0);
      }
    }
  }
  for (const t of model.txs) {
    if (!S.has(t.account) || !t.symbol) continue;
    const i = idx(t.day);
    if (i <= s0 || i > e) continue;
    const x = fxv(t.currency, i);
    if (isNaN(x)) continue;
    const r = K(t.symbol), k = i - s0;
    if (t.type === "BUY" || t.type === "SELL") {
      const pc = model.pPLN(t.symbol, i);
      const sign = t.type === "BUY" ? 1 : -1;
      r.pl[k] += (isNaN(pc) ? 0 : sign * t.qty * pc) + t.amt * x;
      if (t.type === "BUY") r.buy[k] += -t.amt * x;
    } else if (t.type === "DIVIDEND" || t.type === "INTEREST" || t.type === "TAX") r.pl[k] += t.amt * x;
  }
  return { n, keys };
}

// (7) contribution to the result in percentage points: component P&L / average capital employed in the period
// (sum = portfolio P&L / average capital — a capital-weighted return, consistent with the PLN amounts)
export function contributions(model: Model, agg: AggregateResult, s0: number, e: number): Contributions {
  const { n, keys } = dailyByKey(model, agg.accounts, s0, e);
  let cap = 0, cnt = 0;
  for (let i = s0 + 1; i <= e; i++) { const c = agg.V[i - 1] + agg.F[i]; if (c > 1) { cap += c; cnt++; } }
  const avgCap = cnt ? cap / cnt : 0;
  let gross = agg.V[e] - agg.V[s0];
  for (let i = s0 + 1; i <= e; i++) gross -= agg.F[i];
  const rows = Object.values(keys).map((x): ContributionRow => {
    let pl = 0;
    for (let k = 1; k < n; k++) pl += x.pl[k];
    return { key: x.key, name: x.name, group: x.group, isBond: x.isBond, bondKind: x.bondKind, pl, pp: avgCap > 0 ? pl / avgCap : 0 };
  });
  const sumPl = rows.reduce((t, x) => t + x.pl, 0);
  return { rows, avgCap, gross, total: avgCap > 0 ? gross / avgCap : null, other: avgCap > 0 ? (gross - sumPl) / avgCap : 0 };
}

// (8) return of each asset group (TWR in PLN, as you actually held it) per calendar year
export function groupYearReturns(model: Model, agg: AggregateResult, s0: number, e: number): GroupYearReturns {
  const { n, keys } = dailyByKey(model, agg.accounts, s0, e);
  const groups: Record<string, { pl: Float64Array; val: Float64Array; buy: Float64Array }> = {};
  for (const x of Object.values(keys)) {
    const g = (groups[x.group] ||= { pl: new Float64Array(n), val: new Float64Array(n), buy: new Float64Array(n) });
    for (let k = 0; k < n; k++) { g.pl[k] += x.pl[k]; g.val[k] += x.val[k]; g.buy[k] += x.buy[k]; }
  }
  const out: GroupYearReturns = {}; // group -> {year: ret}
  const days = model.days;
  for (const [name, g] of Object.entries(groups)) {
    const yr: Record<string, { f: number; days: number }> = {};
    for (let k = 1; k < n; k++) {
      const den = g.val[k - 1] + g.buy[k];
      if (den < 1) continue;
      const y = days[s0 + k].slice(0, 4);
      const o = (yr[y] ||= { f: 1, days: 0 });
      o.f *= 1 + g.pl[k] / den;
      o.days++;
    }
    out[name] = Object.fromEntries(Object.entries(yr).filter(([, o]) => o.days > 5).map(([y, o]) => [y, { ret: o.f - 1, days: o.days }]));
  }
  return out;
}

// (6) rolling return: for each day — the return over the previous `win` days (nominal and real)
export function rollingReturns(model: Pick<ModelData, "N" | "days" | "cpi">, agg: AggregateResult, win = 365, net = false): RollingPoint[] {
  const r = net ? agg.twrRnet : agg.twrR;
  const N = model.N;
  const first = model.days.findIndex((d, i) => agg.V[i] > 1);
  if (first < 0) return [];
  const idxA = new Float64Array(N);
  idxA[first] = 1;
  for (let i = first + 1; i < N; i++) idxA[i] = idxA[i - 1] * (1 + r[i]);
  const out: RollingPoint[] = [];
  for (let i = first + win; i < N; i++) {
    const nom = idxA[i] / idxA[i - win] - 1;
    const infl = model.cpi ? model.cpi[i] / model.cpi[i - win] - 1 : null;
    out.push({ i, day: model.days[i], nominal: nom, inflation: infl, real: infl === null ? null : (1 + nom) / (1 + infl) - 1 });
  }
  return out;
}

// (9) tax optimisation: holdings at a loss on taxable accounts
export function harvestCandidates(model: Model, accountSet: readonly string[]): HarvestCandidate[] {
  const e = model.idx(model.today);
  const out: HarvestCandidate[] = [];
  for (const [pk, lots] of Object.entries(model.lotsByPos)) {
    const [a, s] = pk.split("|");
    if (!accountSet.includes(a) || model.taxExempt.has(a)) continue;
    if (model.instr[s]?.assetClass === "BOND") continue;
    const open = lots.filter((l) => l.qty > EPS);
    if (!open.length) continue;
    const p = model.pPLN(s, e);
    if (isNaN(p)) continue;
    const qty = open.reduce((t, l) => t + l.qty, 0);
    const cost = open.reduce((t, l) => t + l.costPLN, 0);
    const value = qty * p;
    const lossLots = open.filter((l) => l.qty * p < l.costPLN);
    out.push({ account: a, symbol: s, name: model.instr[s].name, qty, cost, value, gain: value - cost, lots: open.length, lossLots: lossLots.length,
      lossInLots: lossLots.reduce((t, l) => t + (l.qty * p - l.costPLN), 0), price: model.priceArr[s][e], ccy: model.priceCcy[s],
      firstOpen: open[0].openDay });
  }
  return out.sort((x, y) => x.gain - y.gain);
}

// (10) TER — annual fund management fees (default values from KID documents; can be overridden in the app)
export const DEFAULT_TER: Record<string, number> = {
  IE00B4L5Y983: 0.20, // iShares Core MSCI World
  IE00BKM4GZ66: 0.18, // iShares Core MSCI EM IMI
  IE00B4K48X80: 0.12, // iShares Core MSCI Europe
  IE00B5BMR087: 0.07, // iShares Core S&P 500
  IE00BF4RFH31: 0.35, // iShares MSCI World Small Cap
  IE00B4ND3602: 0.12, // iShares Physical Gold ETC
  LU1681045370: 0.20, // Amundi MSCI Emerging Markets
};
export function terCosts(model: Model, accountSet: Iterable<string>, terMap: Readonly<Record<string, number>>): TerCosts {
  const S = new Set(accountSet);
  const e = model.idx(model.today);
  const byKey: Record<string, Omit<TerRow, "annual">> = {}, byYear: Record<string, number> = {};
  for (const a of S) {
    const A = model.acc[a];
    if (!A) continue;
    for (const [s, q] of Object.entries(A.qty)) {
      const ins = model.instr[s];
      if (!ins || ins.assetClass === "BOND" || ins.assetClass === "STOCK") continue;
      const ter = (terMap[ins.isin || s] ?? DEFAULT_TER[ins.isin] ?? null);
      const row = (byKey[ins.key] ||= { key: ins.key, isin: ins.isin, name: ins.name, ter, value: 0, paid: 0, assetClass: ins.assetClass });
      const t = (ter || 0) / 100 / 365;
      for (let i = 0; i <= e; i++) {
        if (!q[i]) continue;
        const v = q[i] * model.pPLN(s, i);
        if (isNaN(v)) continue;
        const c = v * t;
        row.paid += c;
        const y = model.days[i].slice(0, 4);
        byYear[y] = (byYear[y] || 0) + c;
      }
      const ve = q[e] * model.pPLN(s, e);
      if (!isNaN(ve)) row.value += ve;
    }
  }
  const rows = Object.values(byKey).map((r): TerRow => ({ ...r, annual: r.value * (r.ter || 0) / 100 }));
  return { rows, byYear };
}

// (3) IKE / IKZE limits
export const RETIREMENT_LIMITS: Record<number, RetirementLimit> = {
  2019: { ike: 14295, ikze: 5718, ikzeSelf: 8577 },
  2020: { ike: 15681, ikze: 6272.4, ikzeSelf: 9408.6 },
  2021: { ike: 15777, ikze: 6310.8, ikzeSelf: 9466.2 },
  2022: { ike: 17766, ikze: 7106.4, ikzeSelf: 10659.6 },
  2023: { ike: 20805, ikze: 8322, ikzeSelf: 12483 },
  2024: { ike: 23472, ikze: 9388.8, ikzeSelf: 14083.2 },
  2025: { ike: 26019, ikze: 10407.6, ikzeSelf: 15611.4 },
  2026: { ike: 28260, ikze: 11304, ikzeSelf: 16956 },
};
export function accountKind(id: string, cfg: AccountsConfig | null | undefined): string {
  const c = cfg?.accounts?.[id] || {};
  if (c.kind) return c.kind;
  const s = (id + " " + (c.name || "")).toUpperCase();
  if (/IKZE/.test(s)) return "IKZE";
  if (/\bIKE\b|-IKE\b|IKE$/.test(s)) return "IKE";
  return "";
}
export function retirementContrib(model: Model, cfg: AccountsConfig | null | undefined): RetirementContrib {
  const byYear: Record<string, Record<string, RetirementFlows>> = {}; // year -> {IKE: {dep, wd, byAcc}, IKZE: {...}}
  const accs: Record<string, string[]> = { IKE: [], IKZE: [] };
  for (const a of model.accounts) { const k = accountKind(a, cfg); if (k) accs[k].push(a); }
  for (const t of model.txs) {
    const k = accountKind(t.account, cfg);
    if (!k || (t.type !== "DEPOSIT" && t.type !== "WITHDRAWAL")) continue;
    if (t.id?.startsWith("BONDGEN-")) continue; // automatically generated coupon payouts
    const y = t.day.slice(0, 4);
    const o = ((byYear[y] ||= {})[k] ||= { dep: 0, wd: 0, byAcc: {} });
    const v = t.amt * model.fx(t.currency, model.idx(t.day));
    if (v >= 0) { o.dep += v; o.byAcc[t.account] = (o.byAcc[t.account] || 0) + v; } else o.wd -= v;
  }
  return { byYear, accs };
}

export { addDays };
